import React from 'react';
import { InView } from 'react-intersection-observer';

import { useViewerData } from '@xingternal/hub';

import { TrackNwtEventProps, trackNwtEvent } from '@/helpers/nwt-tracking';
import { useBreakpoints } from '@/hooks';

type ContainerProps = {
  onClick?: (e: React.MouseEvent) => void;
};

type WithNWTTrackingProps = {
  nwtTrackingOptions: Pick<TrackNwtEventProps, 'page' | 'event_schema' | 'tracking_token' | 'position'>;
  job: {
    url: string;
    id: string;
  };
};

export const withNWTracking = function <T extends WithNWTTrackingProps & ContainerProps>(
  WrappedComponent: React.ComponentType<ContainerProps & T>
) {
  return (props: T): React.ReactElement => {
    const { data: userData } = useViewerData();
    const userId = userData?.user?.id;

    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      nwtTrackingOptions: { page, event_schema, tracking_token, ...restNwtTrackingOptions } = {
        page: 'jobs/detail',
        event_schema: 'searchresult',
      },
      job: { id } = {},
    } = props;
    const { isMobile } = useBreakpoints();
    const nwtTrackingProps = {
      user_id: userId,
      page,
      event_schema,
      tracking_token,
      channel: isMobile ? 'webm' : 'web',
      item_urn: `surn:x-xing:jobs:posting:${id}`,
      ...restNwtTrackingOptions,
    };

    if (!tracking_token) return <WrappedComponent {...(props as T)} />;

    const onShow = (inView: boolean): void => {
      if (inView) {
        trackNwtEvent({
          ...nwtTrackingProps,
          event: 'entered_viewport',
        });
      }
    };

    const onView = (): void => {
      trackNwtEvent({
        ...nwtTrackingProps,
        event: 'opened',
      });
    };

    const onClick = (e: React.MouseEvent): void => {
      onView();
      if (props?.onClick) props.onClick(e);
    };

    const onContextMenu = (): void => {
      onView();
    };

    const onAuxClick = (): void => {
      onView();
    };

    return (
      <>
        <WrappedComponent {...(props as T)} onClick={onClick} onContextMenu={onContextMenu} onAuxClick={onAuxClick} />
        <InView triggerOnce onChange={onShow} />
      </>
    );
  };
};
